/**
 * processImage — single-pass photo pre-processing pipeline.
 *
 * Accepts JPEG / PNG / WebP / HEIC / HEIF input. Always emits image/jpeg.
 *
 *   1. Detect mime by file header (more reliable than File.type from iOS).
 *   2. HEIC/HEIF → ImageBitmap via heic-to.
 *      Other formats → ImageBitmap via createImageBitmap with EXIF rotation.
 *   3. Resize to fit `maxDimension` (longest side, default 2048).
 *   4. Encode as JPEG at `quality` (default 0.85).
 *   5. Return a File with .jpg extension and image/jpeg type.
 *
 * The pure parts (header detection, filename rewriting) are unit-tested.
 * The browser-API parts (createImageBitmap, canvas) are smoke-tested on
 * real devices.
 */

// heic-to is dynamically imported on-demand so the libheif WASM bundle
// (~3 MB) is only fetched when a user actually uploads HEIC/HEIF input.
// Non-iPhone users never download it.

export type DetectedImageMime =
  | 'image/jpeg'
  | 'image/png'
  | 'image/webp'
  | 'image/heic'
  | 'image/heif'

export interface ProcessOptions {
  /** Longest-side cap in pixels. Default 2048. */
  maxDimension?: number
  /** JPEG quality 0..1. Default 0.85. */
  quality?:      number
}

export const SUPPORTED_INPUT_MIMES: ReadonlySet<string> = new Set<string>([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
])

const HEIF_BRANDS: ReadonlySet<string> = new Set<string>([
  'heic', 'heix',
  'mif1', 'msf1',
  'hevc', 'heis', 'hevx', 'hevm', 'hevs',
])

/**
 * Detect image mime by inspecting the first 12+ bytes of the file.
 * Returns null when the header doesn't match a known format.
 */
export function detectMimeFromBytes(bytes: Uint8Array): DetectedImageMime | null {
  if (bytes.length < 12) return null

  // JPEG: FF D8 FF
  if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) return 'image/jpeg'

  // PNG: 89 50 4E 47 0D 0A 1A 0A
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47 &&
      bytes[4] === 0x0D && bytes[5] === 0x0A && bytes[6] === 0x1A && bytes[7] === 0x0A) {
    return 'image/png'
  }

  // WebP: "RIFF" .... "WEBP"
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46  && bytes[3] === 0x46 &&
      bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
    return 'image/webp'
  }

  // HEIF / HEIC: bytes 4..8 = "ftyp", bytes 8..12 = brand
  if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
    const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11])
    if (HEIF_BRANDS.has(brand)) {
      return brand === 'heic' || brand === 'heix' ? 'image/heic' : 'image/heif'
    }
  }

  return null
}

/**
 * Replace the extension of `name` with `.jpg`. Preserves the basename;
 * defaults to "photo.jpg" for empty / extensionless input.
 */
export function replaceExtensionForJpeg(name: string): string {
  if (!name || name.trim().length === 0) return 'photo.jpg'
  const idx = name.lastIndexOf('.')
  if (idx <= 0) return `${name}.jpg`
  return `${name.slice(0, idx)}.jpg`
}

/**
 * Compute (width, height) so the longest side fits within maxDim,
 * preserving aspect ratio. Both inputs must be positive integers.
 */
export function fitWithin(
  width:  number,
  height: number,
  maxDim: number,
): { width: number; height: number } {
  if (width <= 0 || height <= 0) {
    throw new Error(`fitWithin: invalid input dimensions (${width}x${height})`)
  }
  const longest = Math.max(width, height)
  if (longest <= maxDim) return { width, height }
  const scale = maxDim / longest
  return {
    width:  Math.max(1, Math.round(width  * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

async function readHeader(file: Blob, length = 16): Promise<Uint8Array> {
  const buf = await file.slice(0, length).arrayBuffer()
  return new Uint8Array(buf)
}

async function bitmapFromInput(file: File, mime: DetectedImageMime | null): Promise<ImageBitmap> {
  const looksHeic = mime === 'image/heic' || mime === 'image/heif'

  if (looksHeic) {
    const { heicTo } = await import('heic-to')
    return heicTo({ blob: file, type: 'bitmap' })
  }

  if (mime === null) {
    // Header didn't match a known format — could still be HEIC with an
    // unusual brand. Defer to heic-to's deeper check before giving up.
    const { heicTo, isHeic } = await import('heic-to')
    if (await isHeic(file)) {
      return heicTo({ blob: file, type: 'bitmap' })
    }
  }

  return createImageBitmap(file, { imageOrientation: 'from-image' })
}

/**
 * Run a file through the full pipeline. Always returns a JPEG File whose
 * name has a .jpg extension and whose type is image/jpeg. Throws on input
 * that can't be decoded.
 */
export async function processImage(
  input: File,
  opts:  ProcessOptions = {},
): Promise<File> {
  const maxDim  = opts.maxDimension ?? 2048
  const quality = opts.quality      ?? 0.85

  const header = await readHeader(input)
  const mime   = detectMimeFromBytes(header)

  const bitmap = await bitmapFromInput(input, mime)

  try {
    const { width, height } = fitWithin(bitmap.width, bitmap.height, maxDim)

    const canvas = document.createElement('canvas')
    canvas.width  = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not acquire 2D canvas context')
    ctx.drawImage(bitmap, 0, 0, width, height)

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', quality)
    })
    if (!blob) throw new Error('canvas.toBlob returned null')

    return new File(
      [blob],
      replaceExtensionForJpeg(input.name),
      { type: 'image/jpeg', lastModified: Date.now() },
    )
  } finally {
    bitmap.close()
  }
}
